import React from 'react';
import { currencyColors } from '../../helpers/constant';
import { toFixedTrunc } from '../../../client/Juno/helpers';
import { getCurrencySymbol } from '../../../client/Juno/helpers';
import { LinearProgress, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

const BalancesDetailsCard = ({
    item,
    imageBaseUrl,
    actionIcons,
    handleActionClick,
    setSelectedBalanceData,
    onCardClick,
}) => {
    const isCryptoPage = useSelector(state => state.juno.activeCurrencyPage) === 'crypto';

    const clientData = useSelector(state => state.juno.clientData);
    const { readOnly } = clientData || {};

    return (
        <div
            onClick={() => onCardClick(item?.currencyShortName)}>
            <div className="relative p-6 h-full bg-white dark:!bg-[#26272B] rounded-lg outline outline-1 outline-offset-[-1px] outline-black/10 overflow-hidden flex flex-col">
                <div
                    style={{
                        backgroundColor: currencyColors.find(
                            colorItem => colorItem?.symbol === item?.currencyShortName,
                        )?.color,
                    }}
                    className={`absolute left-[-60px] !opacity-70 top-[-60px] w-40 h-40 blur-[99px] rounded-full z-10`}></div>

                <div className="relative z-2 flex flex-col !gap-3 flex-grow">
                    {/* Top section */}
                    <div className="flex justify-between">
                        <div className="flex">
                            <img
                                src={`${imageBaseUrl}${item?.currencyIcon}`}
                                className="w-10 h-10"
                                alt="currency-icon"
                            />
                            <div className="ml-3 lg:hidden block">
                                <p className="justify-start !text-text text-sm font-semibold">
                                    {item?.currencyFullName}
                                </p>
                                <p className="self-stretch justify-start text-balanceCard-manage text-sm font-medium">
                                    {item?.currencyShortName}
                                </p>
                            </div>
                        </div>
                        <div className="block lg:hidden">
                            <p className="text-right !text-text text-sm font-semibold">
                                <span
                                    className="mr-[2px]"
                                    data-e2e-name={item?.currencyShortName}
                                    data-e2e={
                                        toFixedTrunc(item?.balanceAmount, isCryptoPage ? 8 : 2) || 0
                                    }>
                                    {getCurrencySymbol(item?.currencyShortName)}
                                </span>
                                {toFixedTrunc(item?.balanceAmount, isCryptoPage ? 8 : 2) || 0}
                            </p>
                            {/* becky asked to remove usdAmount */}
                            {/* <p className="self-stretch text-right justify-start text-zinc-600 text-sm font-medium">
                                ${toFixedTrunc(item?.convertedUSDAmount, 2) || 0}
                            </p> */}
                        </div>
                    </div>
                    <div>
                        <p className="justify-start !text-text text-base font-medium hidden lg:block pb-[6px]">
                            {toFixedTrunc(item?.holdingPercentage, 2) || 0}%
                        </p>
                        <div className="justify-start !text-text text-sm font-medium block lg:hidden pb-1">
                            {toFixedTrunc(item?.holdingPercentage, 2) || 0}%
                        </div>
                        <LinearProgress
                            variant="determinate"
                            value={item?.holdingPercentage}
                            sx={{
                                '&&': {
                                    backgroundColor: 'var(--progress-bg)',
                                    '& .MuiLinearProgress-bar': {
                                        transform: 'rotateY(180deg)',
                                        backgroundColor: currencyColors.find(
                                            colorItem =>
                                                colorItem?.symbol === item?.currencyShortName,
                                        )?.color,
                                    },
                                },
                            }}
                        />
                    </div>
                    {/* Bottom Section - Always sticks to bottom due to mt-auto */}
                    <div className="!flex flex-col gap-3 mt-auto">
                        <div className="self-stretch hidden lg:flex gap-2 flex-col">
                            <div className="justify-start text-[28px] font-semibold !text-primary flex gap-2">
                                <p className="!font-inter">
                                    {!isCryptoPage && (
                                        <span
                                            className="mr-[2px]"
                                            data-e2e-currency={item?.currencyShortName}
                                            data-e2e-amount={
                                                toFixedTrunc(
                                                    item?.balanceAmount,
                                                    isCryptoPage ? 8 : 2,
                                                ) || 0
                                            }>
                                            {getCurrencySymbol(item?.currencyShortName)}
                                        </span>
                                    )}
                                    {toFixedTrunc(item?.balanceAmount, isCryptoPage ? 8 : 2) || 0}
                                </p>
                            </div>
                            <div data-e2e-currency={item?.currencyShortName}
                                data-e2e-amount={
                                    toFixedTrunc(item?.balanceAmount, isCryptoPage ? 8 : 2) || 0
                                }
                                className="self-stretch justify-start font-inter text-balanceCard-manage text-xs font-medium mr-2 uppercase">
                                {`${isCryptoPage ? (item?.currencyShortName == 'USDT' ? 'tether' : item?.blockchain) : item?.currencyFullName} (${item?.currencyShortName})`}
                            </div>
                        </div>

                        <div className="hidden lg:flex gap-[6px] !text-text">
                            {actionIcons?.map(iconItem => (
                                <Tooltip
                                    key={iconItem?.key}
                                    arrow
                                    placement="top"
                                    title={iconItem?.tooltip}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                textTransform: 'uppercase',
                                            },
                                        },
                                    }}>
                                    <div
                                        onClick={() => {
                                            if (readOnly) return;
                                            handleActionClick(iconItem?.key);
                                            setSelectedBalanceData(item);
                                        }}
                                        className={clsx("group relative w-11 h-11 !bg-secondary rounded-lg flex justify-center items-center cursor-pointer text-inherit dark:text-white", readOnly && "cursor-not-allowed opacity-50")}>
                                        {iconItem?.icon}
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

BalancesDetailsCard.propTypes = {
    item: PropTypes.object.isRequired,
    imageBaseUrl: PropTypes.string.isRequired,
    actionIcons: PropTypes.array.isRequired,
    handleActionClick: PropTypes.func.isRequired,
    setSelectedBalanceData: PropTypes.func.isRequired,
    onCardClick: PropTypes.func.isRequired,
};

export default BalancesDetailsCard;
